import { NotificationAdd } from '@mui/icons-material'
import { delay } from 'lodash'
import { type FormEvent, useRef } from 'react'
import { StatusButton } from '@/components/StatusButton'
import {
  GaForm,
  type GaFormHandle,
  type GaFormProps,
} from '@/features/analytics/gaForm/GaForm'
import { useDeferredGaSubmit } from '@/features/analytics/gaForm/useDeferredGaSubmit'
import { useAddNotificationMutation } from '@/features/notify-me/api'
import { useSearch } from '@/features/search/hooks/useSearch'
import { encodeCriteriaString } from '@/features/search/maps/criteriaString'
import { useAppSelector } from '@/redux/hooks'

const baseGaFormProps: Omit<GaFormProps, 'children'> = {
  formName: 'notify me',
  formId: 'notify-me',
  formLocation: 'main',
  formDestination: 'emc/notification',
  formSubmitText: 'Notify Me',
}

export const AddNotificationButton = () => {
  const { selectors } = useSearch()
  const activeSearch = useAppSelector(selectors.selectActiveSearchOptions)
  const [addNotification, request] = useAddNotificationMutation()
  const gaFormRef = useRef<GaFormHandle>(null)
  const latestCriteriaRef = useRef<string | null>(null)
  const { markPending } = useDeferredGaSubmit(gaFormRef, request)

  const disabled = !activeSearch

  const handleSubmit = (_e?: FormEvent<HTMLFormElement>) => {
    if (!activeSearch) return

    const criteriaString = encodeCriteriaString(activeSearch)
    const url = `${import.meta.env.VITE_LEGACY_URL}/Notifications.aspx?Page=MyNotifications&Src=Search`

    latestCriteriaRef.current = criteriaString
    markPending()

    addNotification({ searchString: criteriaString })
      .unwrap()
      .then(() => delay(Promise.resolve, 1000))
      .then(() => {
        window.location.href = url
      })
  }

  const gaFormProps: Omit<GaFormProps, 'children'> = {
    ...baseGaFormProps,
    getFormContent: () =>
      latestCriteriaRef.current
        ? `Search String: ${latestCriteriaRef.current}`
        : null,
    formStatus: request.isError
      ? 'error'
      : request.isSuccess
        ? 'success'
        : null,
    deferGaSubmit: true,
    onSubmit: handleSubmit,
  }

  return (
    <GaForm ref={gaFormRef} {...gaFormProps}>
      <StatusButton
        disabled={disabled}
        startIcon={<NotificationAdd />}
        type="submit"
        isError={request.isError}
        isSuccess={request.isSuccess}
        isLoading={request.isLoading}
      >
        Notify Me
      </StatusButton>
    </GaForm>
  )
}
